import { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const variablesSession = [
  { cle: "{session}", desc: "Nom de la session" },
  { cle: "{date_depart}", desc: "Date de départ" },
  { cle: "{date_retour}", desc: "Date de retour" },
  { cle: "{places_total}", desc: "Nombre de places total" },
  { cle: "{places_restantes}", desc: "Places restantes" },
  { cle: "{description}", desc: "Description de la session" },
];
const variablesDevis = [
  { cle: "{session}", desc: "Nom de la session" },
  { cle: "{date_depart}", desc: "Date de départ" },
  { cle: "{date_retour}", desc: "Date de retour" },
  { cle: "{nom_famille}", desc: "Nom de la famille (1er participant)" },
  { cle: "{nb_participants}", desc: "Nombre de participants" },
  { cle: "{liste_participants}", desc: "Liste détaillée des participants" },
  { cle: "{montant_total}", desc: "Montant total" },
  { cle: "{montant_paye}", desc: "Montant déjà versé" },
  { cle: "{reste_a_payer}", desc: "Reste à payer" },
];
const variablesParType = {
  publicite: variablesSession,
  relance_places: variablesSession,
  relance_complete: variablesSession,
  devis: variablesDevis,
};

const quillModules = {
  toolbar: [
    ["bold", "italic", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

function OumrahModeles() {
  const [modeles, setModeles] = useState([]);
  const [selection, setSelection] = useState(null);
  const [contenu, setContenu] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const quillRef = useRef(null);

  const fetchModeles = async () => {
    try {
      const res = await api.get("/oumrah/modeles");
      setModeles(res.data);
      if (res.data.length > 0 && !selection) {
        setSelection(res.data[0]);
        setContenu(res.data[0].contenu || "");
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchModeles(); }, []);

  const choisirModele = (m) => {
    setSelection(m);
    setContenu(m.contenu || "");
    setMessage("");
  };

  const insererVariable = (cle) => {
    const editor = quillRef.current && quillRef.current.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range ? range.index : editor.getLength(), cle);
    } else {
      setContenu((c) => c + cle);
    }
  };

  const enregistrer = async () => {
    if (!selection) return;
    setLoading(true);
    setMessage("");
    try {
      await api.put("/oumrah/modeles/" + selection.type, { contenu });
      setMessage("Modèle enregistré.");
      fetchModeles();
    } catch (err) {
      setMessage(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const variables = selection ? (variablesParType[selection.type] || []) : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white">Modèles</MDTypography>
              </MDBox>
              <MDBox p={2}>
                {modeles.map((m) => (
                  <MDBox
                    key={m.id}
                    onClick={() => choisirModele(m)}
                    sx={{
                      p: 1.5, mb: 1, borderRadius: "8px", cursor: "pointer",
                      bgcolor: selection && selection.id === m.id ? "#e3f2fd" : "transparent",
                      border: selection && selection.id === m.id ? "1px solid #90caf9" : "1px solid #eee",
                    }}
                  >
                    <MDTypography variant="button" fontWeight="medium">{m.libelle}</MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={9}>
            <Card>
              <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="dark" borderRadius="lg" coloredShadow="dark">
                <MDTypography variant="h6" color="white">
                  {selection ? "Éditer : " + selection.libelle : "Sélectionnez un modèle"}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                {selection && (
                  <>
                    <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                      Variables disponibles (cliquez pour insérer) :
                    </MDTypography>
                    <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
                      {variables.map((v) => (
                        <Chip
                          key={v.cle}
                          label={v.cle}
                          size="small"
                          onClick={() => insererVariable(v.cle)}
                          sx={{ cursor: "pointer", bgcolor: "#f0f0f0", "&:hover": { bgcolor: "#e0e0e0" } }}
                          title={v.desc}
                        />
                      ))}
                    </MDBox>
                    <Divider sx={{ my: 2 }} />
                    <MDBox sx={{ ".ql-container": { minHeight: "240px", fontSize: "15px" }, ".ql-editor": { minHeight: "240px" } }}>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={contenu}
                        onChange={setContenu}
                        modules={quillModules}
                      />
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={2} mt={3}>
                      <MDButton color="info" onClick={enregistrer} disabled={loading}>
                        <Icon>save</Icon>&nbsp;{loading ? "Enregistrement..." : "Enregistrer"}
                      </MDButton>
                      {message && (
                        <MDTypography variant="caption" color={message.includes("enregistr") ? "success" : "error"}>
                          {message}
                        </MDTypography>
                      )}
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahModeles;
